import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getDatasourceProcessedToastContent,
  replaceDealItemsInProgressWithSuccess,
} from '@/lib/deal-items-toast';
import {
  DealItemWithVersions,
  GetDatasourceDocument,
  GetDatasourceQuery,
  GetDealItemsByDealIdQuery,
  useDealItemsUpdatedV3Subscription,
  useGetDealItemsByDealIdQuery,
  useGetDealItemsValidationErrorsLazyQuery,
} from '@/lib/graphql/generated/graphql';
import { useUndoDealItems } from '@/lib/hooks/useUndoDealItems';
import { useValidationWorker } from '@/lib/hooks/useValidationWorker';
import { datasourceExcludedUpdatedPromptState } from '@/states/datasource-excluded-updated-prompt.state';
import { dealItemDescriptionsByActiveVersionIdState } from '@/states/deal-item-descriptions.state';
import { dealLoadedForIdAtom } from '@/states/deal.state';
import { pendingOptimisticUpdatesState } from '@/states/pending-optimistic-updates.state';
import {
  backendValidatedProductIdsAtom,
  dealItemsLoadingState,
  dealItemsRefetchState,
  productsMapState,
} from '@/states/products.state';
import {
  ALL_HIGHLIGHTABLE_FIELDS,
  type ProductModification,
  recentlyModifiedProductsState,
  updateRecentlyModifiedProducts,
} from '@/states/recently-modified-products.state';
import type { ApolloClient } from '@apollo/client';
import type { TFunction } from 'i18next';
import { getDefaultStore, useSetAtom, useStore } from 'jotai';

// 1. On extrait le type d'un item tel que renvoyé par la query
type DealItemFromQuery = NonNullable<
  GetDealItemsByDealIdQuery['deal']
>['dealItemsWithVersions'][number];

const priceKeys = [
  'buyingPrice',
  'choosePrice',
  'discountedPrice',
  'listPrice',
  'potentialSalesVolume',
  'shippingPrice',
  'eeePrice',
] as const;

type PriceKey = (typeof priceKeys)[number];

const percentageKeys = [
  'chooseDiscount',
  'marginPercentage',
  'vatRate',
] as const;

type PercentageKey = (typeof percentageKeys)[number];

// 3) Fonctions pour diviser / multiplier les bons champs
function normalizeSingle(item: DealItemFromQuery): DealItemWithVersions {
  return normalize([item])[0];
}

function normalize(items: DealItemFromQuery[]): DealItemWithVersions[] {
  return items.map((item) => {
    const activeVersionCopy: DealItemFromQuery['activeVersion'] = {
      ...item.activeVersion,
    };
    const lastBrandVersionCopy: DealItemFromQuery['lastBrandVersion'] =
      item.lastBrandVersion
        ? {
            ...item.lastBrandVersion,
          }
        : null;
    const lastChooseVersionCopy: DealItemFromQuery['lastChooseVersion'] =
      item.lastChooseVersion
        ? {
            ...item.lastChooseVersion,
          }
        : null;

    priceKeys.forEach((k: PriceKey) => {
      const activeVal = item.activeVersion[k];
      if (typeof activeVal === 'number') activeVersionCopy[k] = activeVal / 100;
      if (lastBrandVersionCopy) {
        const brandVal = lastBrandVersionCopy[k];
        if (typeof brandVal === 'number')
          lastBrandVersionCopy[k] = brandVal / 100;
      }
      if (lastChooseVersionCopy) {
        const chooseVal = lastChooseVersionCopy[k];
        if (typeof chooseVal === 'number')
          lastChooseVersionCopy[k] = chooseVal / 100;
      }
    });

    percentageKeys.forEach((k: PercentageKey) => {
      const activeVal = item.activeVersion[k];
      if (typeof activeVal === 'number') activeVersionCopy[k] = activeVal * 100;
      if (lastBrandVersionCopy) {
        const brandVal = lastBrandVersionCopy[k];
        if (typeof brandVal === 'number')
          lastBrandVersionCopy[k] = brandVal * 100;
      }
      if (lastChooseVersionCopy) {
        const chooseVal = lastChooseVersionCopy[k];
        if (typeof chooseVal === 'number')
          lastChooseVersionCopy[k] = chooseVal * 100;
      }
    });

    return {
      ...item,
      activeVersion: {
        ...activeVersionCopy,
        updatingFields: item.activeVersion.updatingFields,
        validationErrors: [],
      },
      lastBrandVersion: lastBrandVersionCopy,
      lastChooseVersion: lastChooseVersionCopy,
    } as DealItemWithVersions;
  });
}

type DealDataSource = NonNullable<
  NonNullable<GetDatasourceQuery['deal']>['dataSources'][number]
>;

async function showToastFromProcessedDatasource(
  client: ApolloClient<unknown>,
  dealId: string,
  datasourceId: string | null | undefined,
  t: TFunction
): Promise<void> {
  try {
    const { data } = await client.query<GetDatasourceQuery>({
      query: GetDatasourceDocument,
      variables: { dealId },
      fetchPolicy: 'network-only',
    });
    const dataSources = data?.deal?.dataSources ?? [];
    const found = datasourceId
      ? dataSources.find((dataSource) => dataSource.id === datasourceId)
      : null;
    const ds: DealDataSource | null =
      found != null ? (found as DealDataSource) : null;
    const summary =
      ds && 'importSummary' in ds && ds.importSummary ? ds.importSummary : null;
    if (summary && summary.excludedItemsUpdatedIds.length > 0) {
      getDefaultStore().set(datasourceExcludedUpdatedPromptState, {
        dealId,
        dealItemIds: [...summary.excludedItemsUpdatedIds],
      });
    }
    const content = getDatasourceProcessedToastContent(ds, t);
    replaceDealItemsInProgressWithSuccess(content, {
      datasourceId,
      kind: 'datasourceProcessed',
    });
  } catch (error) {
    console.error('Failed to resolve datasource import summary:', error);
    replaceDealItemsInProgressWithSuccess(
      {
        title: t('general.success'),
        description: t(
          'components.deal_items_subscription.toast_datasource_completed'
        ),
      },
      {
        datasourceId,
        kind: 'datasourceProcessed',
      }
    );
  }
}

/** Carte dealItemId → historyId actif pour valider la pile undo contre le cache. */
function historyIdMapFromDealItems(
  items: DealItemFromQuery[]
): Map<string, string> {
  return items.reduce<Map<string, string>>((acc, item) => {
    const historyId = item.activeVersion.historyId;
    if (historyId) {
      acc.set(item.id, historyId);
    }
    return acc;
  }, new Map());
}

// 4) Hook
export function useDealItems(dealId: string) {
  const store = useStore();
  const setProductsMap = useSetAtom(productsMapState);
  const setDealItemsLoading = useSetAtom(dealItemsLoadingState);
  const setDealItemsRefetch = useSetAtom(dealItemsRefetchState);
  const setPendingOptimisticUpdates = useSetAtom(pendingOptimisticUpdatesState);
  const setRecentlyModified = useSetAtom(recentlyModifiedProductsState);
  const { invalidateUndoForItems } = useUndoDealItems(dealId);
  const { validate, markBackendConfirmed } = useValidationWorker();
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useGetDealItemsByDealIdQuery({
    variables: { dealId },
    skip: !dealId,
    fetchPolicy: 'no-cache',
  });

  const [fetchValidationErrors, { data: validationErrorsData }] =
    useGetDealItemsValidationErrorsLazyQuery({
      fetchPolicy: 'no-cache',
    });

  useEffect(() => {
    if (!dealId) return;
    store.set(dealItemDescriptionsByActiveVersionIdState, null);
    store.set(backendValidatedProductIdsAtom, new Set());
  }, [dealId, store]);

  // Expose refetch for external use (e.g., after deletions or imports).
  const refetchDealItems = useCallback(async () => {
    await refetch();
  }, [refetch]);

  useEffect(() => {
    setDealItemsRefetch({ fn: refetchDealItems });
  }, [setDealItemsRefetch, refetchDealItems]);

  // Subscribe to deal items updates to keep cache in sync
  useDealItemsUpdatedV3Subscription({
    variables: { dealId },
    skip: !dealId,
    fetchPolicy: 'no-cache',
    onSubscriptionData: async ({ subscriptionData, client: apolloClient }) => {
      const payload = subscriptionData.data?.dealItemsUpdatedV3;
      const fromAction = payload?.fromAction;
      const updatedItems = payload?.items ?? [];

      // itemsDeleted: server returns empty items (deleted IDs no longer in DB); refetch to sync cache
      if (fromAction?.type === 'itemsDeleted') {
        store.set(dealItemDescriptionsByActiveVersionIdState, null);
        await refetch();
        replaceDealItemsInProgressWithSuccess({
          title: t('general.success'),
          description: t(
            'components.deal_items_subscription.toast_items_deleted'
          ),
        });
        return;
      }

      // datasourceProcessed with empty items: resolve real result via GetDatasource and show toast from importSummary
      if (fromAction?.type === 'datasourceProcessed' && !updatedItems.length) {
        void showToastFromProcessedDatasource(
          apolloClient,
          dealId,
          fromAction.datasourceId ?? undefined,
          t
        );
        return;
      }

      // For other actions, we need items to merge into state
      if (!updatedItems.length) return;

      const fromType = fromAction?.type;
      const shouldInvalidateDescriptionCache =
        fromType === 'datasourceProcessed' || fromType === 'itemsCreated';
      if (shouldInvalidateDescriptionCache) {
        store.set(dealItemDescriptionsByActiveVersionIdState, null);
      }

      // Jotai est la source de vérité pour l'UI — on lit la Map directement,
      // O(k) Map lookups au lieu de readQuery O(n) + writeQuery O(n) Apollo.
      const currentMap = store.get(productsMapState);
      const currentPendingUpdates = store.get(pendingOptimisticUpdatesState);
      const newItems = updatedItems
        .filter((update) => !currentMap.has(update.dealItemWithVersions.id))
        .map((update) => update.dealItemWithVersions);

      const updatedItemIds = new Set(
        updatedItems.map((u) => u.dealItemWithVersions.id)
      );
      const processedPendingIds = new Set<string>();
      currentPendingUpdates.forEach((id) => {
        if (updatedItemIds.has(id) || !currentMap.has(id))
          processedPendingIds.add(id);
      });

      setProductsMap((prev) => {
        const next = new Map(prev);

        for (const update of updatedItems) {
          const normalized = normalizeSingle(update.dealItemWithVersions);
          const prevItem = prev.get(normalized.id);

          let merged: DealItemWithVersions = {
            ...normalized,
            lastBrandVersion:
              normalized.lastBrandVersion ?? prevItem?.lastBrandVersion ?? null,
            lastChooseVersion:
              normalized.lastChooseVersion ??
              prevItem?.lastChooseVersion ??
              null,
          };

          if (currentPendingUpdates.has(normalized.id) && prevItem) {
            merged = {
              ...merged,
              activeVersion: {
                ...merged.activeVersion,
                ...prevItem.activeVersion,
                validationErrors:
                  merged.activeVersion.validationErrors ??
                  prevItem.activeVersion.validationErrors,
              },
            };
          }

          next.set(normalized.id, merged);
        }

        return next;
      });

      if (processedPendingIds.size > 0) {
        setPendingOptimisticUpdates((prev) => {
          const next = new Set(prev);
          processedPendingIds.forEach((id) => next.delete(id));
          return next;
        });
      }

      void fetchValidationErrors({ variables: { dealId } });

      // Mark products and fields as recently modified for highlighting
      // This covers: itemsUpdated, itemsCreated, datasourceProcessed
      // Backend may not send changedFields for itemsCreated/datasourceProcessed
      // (CSV, scraping, copy previous sales) — use all highlightable fields as fallback
      const isBulkImportOrCreate =
        fromAction?.type === 'itemsCreated' ||
        fromAction?.type === 'datasourceProcessed';
      const now = Date.now();
      const modifications: ProductModification[] = updatedItems.map(
        (update) => {
          const backendFields = update.changedFields ?? [];
          const fields =
            backendFields.length > 0
              ? backendFields
              : isBulkImportOrCreate
                ? ALL_HIGHLIGHTABLE_FIELDS
                : [];
          return {
            productId: update.dealItemWithVersions.id,
            fields,
          };
        }
      );

      setRecentlyModified((prev) =>
        updateRecentlyModifiedProducts(prev, now, modifications)
      );

      // Invalidate undo stack entries when items are updated by another source.
      // On ne passe que les items modifiés (pas tout le tableau) — suffisant pour
      // valider les entrées undo concernées.
      if (fromAction?.type === 'itemsUpdated') {
        const historyMap = historyIdMapFromDealItems(
          updatedItems.map((u) => u.dealItemWithVersions)
        );
        if (historyMap.size > 0) {
          invalidateUndoForItems(historyMap);
        }
      }

      // Success toasts: subscription is the single source for itemsCreated,
      // datasourceProcessed, itemsDeleted. "En cours" toasts are shown at
      // mutation launch in each caller; we never duplicate success here.
      // (itemsUpdated: no toast to avoid noise)
      if (fromAction?.type === 'itemsCreated' && newItems.length > 0) {
        const key =
          newItems.length === 1
            ? 'components.deal_items_subscription.toast_items_created_one'
            : 'components.deal_items_subscription.toast_items_created_other';
        replaceDealItemsInProgressWithSuccess(
          {
            title: t('general.success'),
            description: t(key, { count: newItems.length }),
          },
          {
            createdItemId: newItems.length === 1 ? newItems[0]?.id : undefined,
            kind: 'itemsCreated',
            fallbackToToast: false,
          }
        );
      }
      if (fromAction?.type === 'datasourceProcessed') {
        void showToastFromProcessedDatasource(
          apolloClient,
          dealId,
          fromAction.datasourceId ?? undefined,
          t
        );
      }
    },
  });

  // On navigation remount with same dealId, productsMap already has items → skip the full-page loader.
  // Check dealLoadedForIdAtom to avoid skipping the loader when navigating to a different deal.
  const hasExistingItems =
    store.get(productsMapState).size > 0 &&
    store.get(dealLoadedForIdAtom) === dealId;
  const initialLoading = loading && !data && !hasExistingItems;

  useEffect(() => {
    setDealItemsLoading({ initialLoading, error });
  }, [initialLoading, error, setDealItemsLoading]);

  useEffect(() => {
    if (!data || !data.deal) return;
    if (data.deal.id !== dealId) return;
    validate(data.deal.dealItemsWithVersions);
    void fetchValidationErrors({ variables: { dealId } });
    const normalizedItems = normalize(data.deal.dealItemsWithVersions);
    setProductsMap((prev) => {
      const next = new Map<string, DealItemWithVersions>();
      for (const n of normalizedItems) {
        const prevItem = prev.get(n.id);
        const lastBrandVersion =
          n.lastBrandVersion ?? prevItem?.lastBrandVersion ?? null;
        const lastChooseVersion =
          n.lastChooseVersion ?? prevItem?.lastChooseVersion ?? null;
        const validationErrors =
          prevItem?.activeVersion.id === n.activeVersion.id
            ? prevItem.activeVersion.validationErrors
            : [];
        next.set(n.id, {
          ...n,
          lastBrandVersion,
          lastChooseVersion,
          activeVersion: { ...n.activeVersion, validationErrors },
        });
      }
      return next;
    });
  }, [data, setProductsMap, validate, fetchValidationErrors, dealId]);

  useEffect(() => {
    if (!validationErrorsData?.deal) return;
    if (validationErrorsData.deal.id !== dealId) return;
    const { dealItemsWithVersions } = validationErrorsData.deal;
    const updatedIds: string[] = [];
    setProductsMap((prev) => {
      const next = new Map(prev);
      dealItemsWithVersions.forEach((item) => {
        const currentItem = prev.get(item.id);
        if (currentItem?.activeVersion.id === item.activeVersion.id) {
          updatedIds.push(item.id);
          next.set(item.id, {
            ...currentItem,
            activeVersion: {
              ...currentItem.activeVersion,
              validationErrors: item.activeVersion.validationErrors,
            },
          });
        }
      });
      return next;
    });
    markBackendConfirmed(updatedIds);
  }, [validationErrorsData, setProductsMap, markBackendConfirmed, dealId]);

  return {
    loading,
    error,
    refetch: refetchDealItems,
    initialLoading,
  };
}
